// @ts-nocheck
import React, { useState, useEffect } from "react";
import IframeRoutes from "./routes/IframeRoutes";
import { useLocation } from "react-router-dom";
import ErrorHandler from "components/common/ErrorHandler";
import { errorState } from "recoil/error/atom";
import { modalState } from "recoil/modal/atom";
import ModalComponent from "components/common/ModalComponent";
import { toastMessageState } from "recoil/toast/atom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { setcompanyDetails } from "recoil/companyDetails/selector";
import { getEmployeeAuthorisation } from "swr/auth";
import { setCurrentOrganisationDetails } from "recoil/organisations/selector";
import { setDocumentCategories } from "recoil/documentCategories/selector";
import { setNoteTypes } from "recoil/noteTypes/selector";
import { ToastHandler } from "@qjumpers/common-ui";
import { jwtDecode } from 'jwt-decode';
import { getRulesDetails } from "utils/authHelper";
import LoadingWidget from "./widgets/loader";
import configDetails from "constants/config";
import { AuthProvider, useAuth } from "react-oidc-context";
import { setTokenProvider } from "./middleware/api";
import { loadDocumentCategoriesByOrg } from "swr/documentcategory";
import { loadNoteTypes } from "swr/notes";
import 'react-toastify/dist/ReactToastify.css';
import { RecoilRoot } from "recoil";

// Update your IframeApp with immediate token setting
function IframeApp() {
  const location = useLocation();
  const error = useRecoilValue(errorState);
  const modal = useRecoilValue(modalState);
  const toast = useRecoilValue(toastMessageState);
  const setToast = useSetRecoilState(toastMessageState);

  // Simplified state
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [currentCompanyDetails, setCurrentCompanyDetails] = useState<{ id?: string; accountingName?: string;[key: string]: unknown }>();
  const [employeeAuthorization, setEmployeeAuthorizationState] = useState<unknown>(null);
  const [rulesDetails, setRulesDetails] = useState<unknown>();
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  // Recoil setters
  const setCurrentOrgValue = useSetRecoilState(setCurrentOrganisationDetails);
  const setCompanyDetailsState = useSetRecoilState(setcompanyDetails);
  const setDocumentCategoriesData = useSetRecoilState(setDocumentCategories);
  const setNoteTypesData = useSetRecoilState(setNoteTypes);

  // Simplified authentication listener
  useEffect(() => {
    const handleTokenMessage = async (event) => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("jwt") || event.data?.access_token;

      if (!token) {
        return;
      }

      try {
        // Validate token and get company details
        const response = await fetch(
          `${configDetails.ADMIN.apiHost}${configDetails.ADMIN.apiPath}/validate/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.tokens && data.organisation) {
          const { tokens, organisation: currentCompany } = data;

          // ✅ Set token provider IMMEDIATELY - this is crucial
          await setTokenProvider(() => ({
            access_token: tokens.access_token,
            expires_at: Date.now() + tokens.expires_in * 1000,
          }));

          // Store for OIDC context
          // localStorage.setItem("qjumpers.adminui.auth.token", tokens.access_token);
          localStorage.setItem("company-id", currentCompany.id);
          localStorage.setItem("company-name", currentCompany.accountingName);

          // Create and store OIDC user
          const decodedToken = jwtDecode(tokens.access_token);
          const oidcUser = {
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token,
            expires_at: decodedToken.exp,
            profile: {
              sub: decodedToken.sub,
              email: decodedToken.email,
              name: decodedToken.name,
              company_id: currentCompany.id,
              company_name: currentCompany.accountingName,
            },
          };
          
          const userStorageKey = `oidc.user:${configDetails.KEYCLOAK.apiHost}${configDetails.KEYCLOAK.apiPath}/realms/${configDetails.realm}/:qjumpers`;
          sessionStorage.setItem(userStorageKey, JSON.stringify(oidcUser));

          // Set company details
          setCurrentCompanyDetails(currentCompany);
          setCurrentOrgValue(currentCompany);
          setCompanyDetailsState(currentCompany);

          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } catch (err) {
        setIsAuthorized(false);
      }
    };

    window.addEventListener('message', handleTokenMessage);
    handleTokenMessage({}); // Check URL params immediately

    return () => window.removeEventListener('message', handleTokenMessage);
  }, [location.search, setCurrentOrgValue, setCompanyDetailsState]);

  useEffect(() => {
    const loadData = async () => {
      // Wait for basic auth and company details first
      if (!isAuthorized || !currentCompanyDetails) {
        return;
      }

      // Don't wait for auth.user.access_token - we have the token from validation
      if (dataLoaded) {
        return;
      }

      try {
        // Load employee authorization first
        const companyId = currentCompanyDetails.id as string;
        const authResult = await getEmployeeAuthorisation(companyId);
        if (authResult?.data) {
          setEmployeeAuthorizationState(authResult.data);
        } else {
          // Set empty object to prevent blocking
          setEmployeeAuthorizationState({});
        }

        // Load document categories and note types in parallel
        const [docCategories, noteTypes] = await Promise.all([
          loadDocumentCategoriesByOrg(companyId).catch(() => {
            return [];
          }),
          loadNoteTypes(companyId).catch(() => {
            return [];
          }),
        ]);

        setDocumentCategoriesData(docCategories);
        setNoteTypesData({ data: noteTypes });

        // Mark data as loaded
        setDataLoaded(true);

      } catch (err) {
        // Set fallback data to prevent infinite loading
        setEmployeeAuthorizationState({});
        setDocumentCategoriesData({ data: [] });
        setNoteTypesData({ data: [] });
        setDataLoaded(true);
      }
    };

    loadData();
  }, [isAuthorized, currentCompanyDetails, dataLoaded, setDocumentCategoriesData, setNoteTypesData]);

  // Set rules when all data is ready
  useEffect(() => {
    if (currentCompanyDetails && employeeAuthorization !== null && !rulesDetails) {
      try {
        const rules = getRulesDetails(currentCompanyDetails, currentCompanyDetails, employeeAuthorization || {});
        setRulesDetails(rules);
      } catch (err) {
        // Set fallback rules
        setRulesDetails({ companyDetails: currentCompanyDetails });
      }
    }
  }, [currentCompanyDetails, employeeAuthorization, rulesDetails]);

  const clearToastMessage = () => setToast([]);

  const isAppReady = employeeAuthorization !== null && rulesDetails && dataLoaded;

  return (
    <div className="App">
      <IframeTokenProvider>
        {isAuthorized ? (
          <>
            <ErrorHandler errors={error} />
            <ModalComponent modalData={modal} />
            <ToastHandler toast={toast} clearToasts={clearToastMessage} />
            {isAppReady ? (
              <IframeRoutes rulesDetails={rulesDetails} />
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <LoadingWidget color="grey" />
                <div>
                  Loading application data...
                </div>
              </div>
            )}
          </>
        ) : isAuthorized === null ? (
          <div style={{ textAlign: "center", padding: "5rem" }}>
            <LoadingWidget color="grey" />
            <div>Authenticating user...</div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "5rem" }}>
            <div>Unauthorized</div>
          </div>
        )}
      </IframeTokenProvider>
    </div>
  );
}

// Simplified TokenProvider - just for backup
const IframeTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  React.useEffect(() => {
    // This is now backup - token should already be set in handleTokenMessage
    if (auth?.user?.access_token) {
      setTokenProvider(() => {
        if (!auth?.user) {
          return null;
        }
        return {
          access_token: auth.user.access_token,
          expires_at: auth.user.expires_at,
          renew: async () => {
            // Forces silent renew via react-oidc-context
            await auth.signinSilent?.();
            return auth.user;
          },
          logout: async (options = {}) => {
            try {
              if (options.silent && auth.signoutSilent) {
                await auth.signoutSilent();
              } else {
                await auth.signoutRedirect();
              }
            } catch (err) {
            }
          },
        };
      });
    }
  }, [auth.user, auth.signinSilent, auth]);

  return children;
};

// Simplified wrapper
const IframeAuthWrapper = (props: Record<string, unknown>) => {
  const oidcConfig = {
    authority: `${configDetails.KEYCLOAK.apiHost}${configDetails.KEYCLOAK.apiPath}/realms/${configDetails.realm}/`,
    client_id: `${configDetails.realm}`,
    redirect_uri: window.location.origin,
    response_type: "code",
    automaticSilentRenew: false,
    loadUserInfo: false,
    monitorSession: false,
  };

  const onSignoutCallback = () => {
    // Only clear application-specific data
    sessionStorage.clear();
  };

  return (
    <RecoilRoot>
      <AuthProvider {...oidcConfig} onSignoutCallback={onSignoutCallback}>
        <IframeApp {...props} />
      </AuthProvider>
    </RecoilRoot>
  );
};

export default IframeAuthWrapper;